import { useApiClient, useGet, usePost } from "@albertine/api-client";
import { type ServiceLanguage, ServiceLanguageMap } from "@albertine/i18n";
import type { Supplier } from "@albertine/types";
import {
    FitScoreAnalysis,
    type FitScoreAnalysisProps,
} from "@prototypes/ai-recommendations/FitScoreAnalysis";
import { ImageStrip } from "@prototypes/image-strip/ImageStrip";
import * as Sentry from "@sentry/react";
import { Lib } from "albertine-shared-web";
import classNames from "classnames";
import type React from "react";
import { useMemo, useState } from "react";
import { type CatalogSourceKey, CatalogSources } from "../../common/CatalogCategories";
import {
    type CatalogImage,
    type CatalogItem,
    findCatalogItemById,
    getCatalogItemKeyDetail,
} from "../../common/CatalogItem";
import { useAgentsMap } from "../../common/hooks/useAgents";
import { useMembersMap } from "../../common/hooks/useMembers";
import { excludeUndefined } from "../../common/utils/array.util";
import {
    isRecentlyOpened,
    resolveCityGuideLocation,
    showRecentlyOpened,
} from "../../common/utils/catalog.util";
import {
    googleAddressSearchUrl,
    googlePlaceIdUrl,
} from "../../common/utils/google/GooglePlaceService";
import { cdnImage } from "../../common/utils/image.util";
import { defaultDateTimeFormatter } from "../../common/utils/time.util";
import { EventEmitter } from "../../EventEmitter";
import { nameWithoutTitle } from "../../utils/members.util";
import routes from "../../utils/routes";
import { sortAbc } from "../../utils/sort.util";
import Button from "../Common/Buttons";
import { FormattedDateTime } from "../Common/DateTime";
import { Flex, FlexInline } from "../Common/Flex";
import Icon, { CategoryIconMap, type MdIconName, PictogramIcon } from "../Common/Icon";
import InlineTextDelimiter from "../Common/InlineTextDelimiter";
import Label from "../Common/Label";
import Paragraph from "../Common/Paragraph";
import MemberRatings from "../Common/rating/MemberRatings";
import Tag, { ScoreTag } from "../Common/Tag";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { TitleValuePair, TitleValuePairs } from "../MemberData/TitleValuePairs";
import "./CatalogItemView.css";
import type { CatalogItemFull } from "@albertine/types/openapi/lmt";
import { formatTenantName } from "@/common/utils/tenant.util";
import { categoryDisplayName } from "../../common/types/OfferingCategory";
import { hasNonNullProperty } from "../../common/utils/object.util";
import { singularPlural } from "../../common/utils/time.formatted.util";
import TranslationTabs from "../Common/TranslationTabs";
import { Badge } from "../ui/badge";
import { CatalogItemDetailsPrimary } from "./CatalogItemDetails/CatalogItemDetailsPrimary";
import { LanguageTab } from "./CatalogItemDetails/LanguageTab";
import { DistanceBadges } from "./DistanceBadges";
import GoogleMap from "./GooglePlace/GoogleMap";
import { useCatalogItemPlaceDetails } from "./hooks/useCatalogItemPlaceDetails";
import TagsWithCategories from "./TagsWithCategories";

export function parseViewDetails(
    catalogItem: CatalogItemFull,
    supplierData: Supplier[],
): ViewDetails {
    const {
        address,
        googlePlaceId,
        locationGeoPoint,
        images,
        offeringCategories,
        supplierLmtIds,
        source,
        tags,
        website,
        phoneNumber,
    } = catalogItem;
    const keyDetail = getCatalogItemKeyDetail(catalogItem);

    return {
        address: address || undefined,
        addressUrl: address ? googleAddressSearchUrl(address) : undefined,

        googlePlaceUrl: googlePlaceId ? googlePlaceIdUrl(googlePlaceId) : undefined,
        geoPoint: locationGeoPoint ?? undefined,
        images: images ?? [],
        offeringCategories:
            offeringCategories && offeringCategories.length !== 0
                ? {
                      primaryIcon: CategoryIconMap[offeringCategories[0]],
                      text: (offeringCategories || [])
                          .map((category) => categoryDisplayName(category, "en"))
                          .join(", "),
                  }
                : {
                      primaryIcon: CategoryIconMap.other,
                      text: undefined,
                  },
        phoneNumber: phoneNumber ?? undefined,
        suppliersData:
            supplierLmtIds.length !== 0
                ? supplierLmtIds
                      .map((supplierId) => {
                          const supplier = supplierData.find(
                              (it) => it.id === supplierId,
                          );

                          return supplier ?? undefined;
                      })
                      .filter(excludeUndefined)
                : undefined,
        source: CatalogSources[source as CatalogSourceKey]
            ? CatalogSources[source as CatalogSourceKey]
            : undefined,
        tags: tags && tags.length !== 0 ? tags : undefined,
        website: website ?? undefined,
        keyDetail: keyDetail ?? undefined,
    };
}

function InternalDetails(
    props: Pick<
        CatalogItem,
        | "offeringCategories"
        | "notes"
        | "source"
        | "albertineVerified"
        | "recentlyOpened"
    > & {
        suppliersData: Supplier[] | undefined;
    },
) {
    const {
        offeringCategories,
        notes,
        source,
        suppliersData,
        albertineVerified,
        recentlyOpened,
    } = props;
    return (
        <TitleValuePairs>
            <TitleValuePair
                title={<Label.Small.SemiBold>Source</Label.Small.SemiBold>}
                value={
                    <Label.Medium.Light>
                        {source ?? (
                            <Label.Medium.Light.N60>No source</Label.Medium.Light.N60>
                        )}
                    </Label.Medium.Light>
                }
            />
            <TitleValuePair
                title={<Label.Small.SemiBold>Suppliers</Label.Small.SemiBold>}
                value={
                    <Flex.Column gap="medium">
                        {suppliersData ? (
                            suppliersData.sort(sortAbc<Supplier>("name")).map((it) => (
                                <Flex.Column gap="small" key={it.id}>
                                    <Paragraph.Medium.Light>
                                        {it.name}
                                    </Paragraph.Medium.Light>
                                    <a
                                        href={it.website}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <Label.Medium.Light.N50>
                                            {it.website}
                                        </Label.Medium.Light.N50>
                                    </a>
                                </Flex.Column>
                            ))
                        ) : (
                            <Label.Medium.Light.N60>
                                Not added yet
                            </Label.Medium.Light.N60>
                        )}
                    </Flex.Column>
                }
            />

            <TitleValuePair
                title={<Label.Small.SemiBold>Notes</Label.Small.SemiBold>}
                value={
                    <Paragraph.Medium.Light>
                        {notes ?? (
                            <Label.Medium.Light.N60>
                                Not added yet
                            </Label.Medium.Light.N60>
                        )}
                    </Paragraph.Medium.Light>
                }
            />

            {showRecentlyOpened(offeringCategories) && (
                <TitleValuePair
                    title={<Label.Small.SemiBold>Recently opened</Label.Small.SemiBold>}
                    value={
                        <Paragraph.Medium.Light>
                            {isRecentlyOpened(recentlyOpened) ? (
                                <Tag.Small.P90>Yes</Tag.Small.P90>
                            ) : (
                                <Tag.Small.N90>No</Tag.Small.N90>
                            )}
                        </Paragraph.Medium.Light>
                    }
                />
            )}

            <TitleValuePair
                title={<Label.Small.SemiBold>Albertine verified</Label.Small.SemiBold>}
                value={
                    <Paragraph.Medium.Light>
                        {albertineVerified ? (
                            <Tag.Small.P90>Yes</Tag.Small.P90>
                        ) : (
                            <Tag.Small.N90>No</Tag.Small.N90>
                        )}
                    </Paragraph.Medium.Light>
                }
            />
        </TitleValuePairs>
    );
}

function WorldOfAlbertineDetails(
    props: Pick<CatalogItemFull, "published" | "discoverableForTenant">,
) {
    const { published = false } = props;
    const discoverableForTenantText = props.discoverableForTenant
        ? formatTenantName(props.discoverableForTenant)
        : "All Tenants";

    return (
        <TitleValuePairs>
            <TitleValuePair
                title={<Label.Small.SemiBold>Visibility</Label.Small.SemiBold>}
                value={
                    <div className="flex w-full flex-row flex-wrap items-center gap-2">
                        <Badge
                            variant={published ? "green" : "secondary"}
                            size="default"
                        >
                            {published ? "Published" : "Unpublished"}
                        </Badge>
                        <Badge variant="blue" size="default">
                            {discoverableForTenantText}
                        </Badge>
                    </div>
                }
            />
        </TitleValuePairs>
    );
}

function AdminDetails(props: {
    item: CatalogItemFull;
    isAdmin: boolean;
    onView?: () => void;
    onEdit?: () => void;
    onToggleArchive: (item: CatalogItemFull) => void;
}) {
    const { item, isAdmin, onView, onEdit, onToggleArchive } = props;
    const {
        id,
        lmtId,
        archived,
        googlePlaceId,
        googlePlaceName,
        storyblokArticleId,
        storyblokArticleSlug,
        createdAt,
        createdByAgentId,
        updatedAt,
        updatedByAgentId,
        sharingLog,
    } = item;
    const [loading, setLoading] = useState<boolean>(false);
    const membersMap = useMembersMap();
    const agentsMap = useAgentsMap();
    const cityGuideLocation = resolveCityGuideLocation(item.addressComponents);
    const { apiClient } = useApiClient();
    const { mutateAsync: archiveCatalogItem } = usePost("archiveCatalogItem");
    const { mutateAsync: unarchiveCatalogItem } = usePost("unarchiveCatalogItem");
    async function toggleItemArchived() {
        if (
            !archived &&
            !window.confirm("Are you sure you want to archive this catalog item?")
        ) {
            return;
        }

        try {
            setLoading(true);
            if (archived) {
                await unarchiveCatalogItem({ param: { id: lmtId } });
            } else {
                await archiveCatalogItem({ param: { id: lmtId } });
            }

            const updatedItem = await findCatalogItemById(apiClient, id);
            if (!updatedItem) {
                throw new Error("Failed to load updated catalog item");
            }
            onToggleArchive(updatedItem);

            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    type: "info",
                    message: `Item successfully ${
                        archived ? "unarchived" : "archived"
                    }`,
                },
            });
        } catch (error) {
            Sentry.captureException(error, {
                extra: {
                    message: `Failed to ${archived ? "unarchive" : "archive"} catalog item`,
                },
            });
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    message: `${
                        archived ? "Unarchiving" : "Archiving"
                    } catalog item failed`,
                    type: "error",
                },
            });
        } finally {
            setLoading(false);
        }
    }

    const sharing = (
        <>
            {sharingLog.length === 0 && (
                <Label.Medium.Light.N50>-</Label.Medium.Light.N50>
            )}
            {sharingLog
                .sort(
                    Lib.Utils.Sorting.sortByDateComparator((log) =>
                        log?.sharedAt ? new Date(log.sharedAt) : undefined,
                    ),
                )
                .map((entry) => (
                    <a
                        key={`${id}-${entry.messageId}`}
                        href={
                            entry.requestId
                                ? routes.requestWithMessage(
                                      entry.requestId,
                                      entry.messageId,
                                  )
                                : routes.memberWithMessage(
                                      entry.memberId,
                                      entry.messageId,
                                  )
                        }
                        target="_blank"
                        rel="noreferrer"
                    >
                        <Flex.Row
                            key={`${id}-${entry.messageId}`}
                            gap="medium"
                            className="pictogram-icon--hover"
                        >
                            {entry.sharedByAgent && entry.sharedByAgentId && (
                                <>
                                    <PictogramIcon.ArrowForward.Small.T90 />

                                    <FlexInline gap="small">
                                        <FormattedDateTime.Default
                                            date={new Date(entry.sharedAt)}
                                        />
                                        <InlineTextDelimiter />
                                        <Label.Small.Light.N10>
                                            shared with{" "}
                                            {membersMap[entry.memberId]
                                                ? nameWithoutTitle(
                                                      membersMap[entry.memberId],
                                                  )
                                                : entry.memberId}
                                        </Label.Small.Light.N10>
                                    </FlexInline>
                                </>
                            )}

                            {!entry.sharedByAgent && (
                                <>
                                    <PictogramIcon.ArrowBack.Small.P90 />

                                    <FlexInline gap="small">
                                        {defaultDateTimeFormatter(
                                            new Date(entry.sharedAt),
                                        )}
                                        <InlineTextDelimiter />
                                        <Label.Small.Light.N10>
                                            {membersMap[entry.memberId]
                                                ? nameWithoutTitle(
                                                      membersMap[entry.memberId],
                                                  )
                                                : entry.memberId}{" "}
                                            showed interest
                                        </Label.Small.Light.N10>
                                    </FlexInline>
                                </>
                            )}

                            <Icon.OpenInNew.XSmall.N10 />
                        </Flex.Row>
                    </a>
                ))}
        </>
    );

    return (
        <>
            <TitleValuePairs
                className={classNames(archived && "catalog-item--archived")}
            >
                <TitleValuePair
                    title={<Label.Small.SemiBold>Last updated</Label.Small.SemiBold>}
                    value={
                        <Flex.Row gap="xsmall">
                            <FormattedDateTime.Default date={new Date(updatedAt)} />
                            <InlineTextDelimiter />
                            {agentsMap[updatedByAgentId]
                                ? agentsMap[updatedByAgentId].name
                                : updatedByAgentId}
                        </Flex.Row>
                    }
                />

                <TitleValuePair
                    title={<Label.Small.SemiBold>Created</Label.Small.SemiBold>}
                    value={
                        <Flex.Row gap="xsmall">
                            <FormattedDateTime.Default date={new Date(createdAt)} />
                            <InlineTextDelimiter />
                            {agentsMap[createdByAgentId]
                                ? agentsMap[createdByAgentId].name
                                : updatedByAgentId}
                        </Flex.Row>
                    }
                />

                <TitleValuePair
                    title={<Label.Small.SemiBold>Catalog ID</Label.Small.SemiBold>}
                    value={id}
                />

                <TitleValuePair
                    title={<Label.Small.SemiBold>Google Place ID</Label.Small.SemiBold>}
                    value={
                        googlePlaceId || (
                            <Label.Small.Light.N60>
                                Not attached to Google
                            </Label.Small.Light.N60>
                        )
                    }
                />

                <TitleValuePair
                    title={
                        <Label.Small.SemiBold>Google Place Name</Label.Small.SemiBold>
                    }
                    value={
                        googlePlaceName || (
                            <Label.Small.Light.N60>
                                Update linked location for the item to propagate this
                            </Label.Small.Light.N60>
                        )
                    }
                />

                <TitleValuePair
                    title={
                        <Label.Small.SemiBold>
                            Storyblok article ID
                        </Label.Small.SemiBold>
                    }
                    value={
                        storyblokArticleId || (
                            <Label.Small.Light.N60>
                                Not attached to Storyblok
                            </Label.Small.Light.N60>
                        )
                    }
                />

                <TitleValuePair
                    title={<Label.Small.SemiBold>Storyblok slug</Label.Small.SemiBold>}
                    value={
                        storyblokArticleSlug || (
                            <Label.Small.Light.N60>
                                Not attached to Storyblok
                            </Label.Small.Light.N60>
                        )
                    }
                />
                <TitleValuePair
                    title={<Label.Small.SemiBold>City, country</Label.Small.SemiBold>}
                    value={
                        <Paragraph.Medium.Light>
                            {cityGuideLocation ? (
                                <>
                                    {cityGuideLocation.city},{" "}
                                    {cityGuideLocation.country}
                                </>
                            ) : (
                                <Tag.Small.N90>Not added</Tag.Small.N90>
                            )}
                        </Paragraph.Medium.Light>
                    }
                />
                <TitleValuePair
                    title={<Label.Small.SemiBold>Sharing log</Label.Small.SemiBold>}
                    value={<Flex.Column gap="small">{sharing}</Flex.Column>}
                />
            </TitleValuePairs>

            <Flex.Row gap="medium">
                {isAdmin && (
                    <>
                        <Button.E90
                            disabled={loading}
                            showLoadingSpinner={loading}
                            onClick={() => toggleItemArchived()}
                        >
                            <Icon.Delete.Small.E10 />
                            {archived ? "Unarchive" : "Archive"}
                        </Button.E90>

                        {onView && !archived && (
                            <Button
                                onClick={() => {
                                    onView();
                                }}
                            >
                                <Icon.OpenInNew.Small.N10 /> Open in full screen
                            </Button>
                        )}

                        {onEdit && (
                            <Button
                                onClick={() => {
                                    onEdit();
                                }}
                            >
                                <Icon.Edit.Small.N10 /> Edit
                            </Button>
                        )}
                    </>
                )}
            </Flex.Row>
        </>
    );
}

type CatalogItemProps = {
    item: CatalogItemFull;
    mapId: string;
    googlePlaceService: google.maps.places.PlacesService;
    className?: string;
    isAdmin: boolean;
    suppliers: Supplier[];
    distanceOrigin?: { lat: number; lng: number };
    recommendationFitAnalysis?: FitScoreAnalysisProps;
    onView?: (viewableItem: CatalogItemFull) => void;
    onEdit?: (viewableItem: CatalogItemFull) => void;
    onToggleArchive: (item: CatalogItemFull) => void;
};

type ViewDetails = {
    address: string | undefined;
    addressUrl: string | undefined;
    googlePlaceUrl: string | undefined;
    geoPoint:
        | {
              latitude: number;
              longitude: number;
          }
        | undefined;
    images: CatalogImage[];
    offeringCategories: {
        primaryIcon: MdIconName;
        text: string | undefined;
    };
    phoneNumber: string | undefined;
    suppliersData: Supplier[] | undefined;
    source: string | undefined;
    tags: string[] | undefined;
    website: string | undefined;
    keyDetail: string | undefined;
};

function CatalogItemView(props: CatalogItemProps) {
    const {
        item,
        className,
        suppliers,
        googlePlaceService,
        mapId,
        isAdmin,
        distanceOrigin,
        recommendationFitAnalysis,
        onView,
        onEdit,
        onToggleArchive,
    } = props;

    const { geoPoint, images, suppliersData, source, tags } = parseViewDetails(
        item,
        suppliers,
    );

    const feedbackAnchor = useMemo(
        () => `${item.googlePlaceId}__feedback`,
        [item.googlePlaceId],
    );

    const { data: placeDetails } = useCatalogItemPlaceDetails(item, googlePlaceService);
    const { data: reviewsData } = useGet("catalogItemReviews", {
        param: { id: item.lmtId },
    });
    const reviews =
        reviewsData && reviewsData.ratingsCount > 0 && reviewsData.average != null
            ? {
                  ratingsCount: reviewsData.ratingsCount,
                  average: reviewsData.average,
              }
            : undefined;

    const de = item.translations.de;
    const fr = item.translations.fr;

    const translations: Record<ServiceLanguage, React.ReactNode> = {
        en: <LanguageTab item={item} translation={item} />,
        de: hasNonNullProperty(de) ? (
            <LanguageTab item={item} translation={de} />
        ) : undefined,
        fr: hasNonNullProperty(fr) ? (
            <LanguageTab item={item} translation={fr} />
        ) : undefined,
    };

    return (
        <Flex.Column gap="large" className={classNames("catalog-view", className)}>
            <Flex.Column gap="medium" className="catalog-item__main-content">
                <ImageStrip
                    images={images.map((image) => ({
                        id: image.id,
                        src: cdnImage(image.url, { height: 500, width: 0 }),
                        href: image.url,
                        alt: "",
                    }))}
                />

                <Flex.Column className="catalog-item__details">
                    <div className="pt-4">
                        <DistanceBadges item={item} mapCenter={distanceOrigin} />
                    </div>

                    <CatalogItemDetailsPrimary
                        item={item}
                        googlePlaceService={googlePlaceService}
                    />

                    {recommendationFitAnalysis && (
                        <FitScoreAnalysis
                            {...recommendationFitAnalysis}
                            className="pt-4"
                        />
                    )}

                    <Flex.Column>
                        <Flex.Column
                            gap="large"
                            className={classNames(
                                item.archived && "catalog-item--archived",
                            )}
                        >
                            <TranslationTabs
                                translationOptions={[]}
                                tabs={Object.entries(translations)
                                    .filter(([_key, value]) => value)
                                    .map(([key, value]) => ({
                                        key: key as ServiceLanguage, // TODO: Remove the need for setting type
                                        text:
                                            ServiceLanguageMap[
                                                key as ServiceLanguage
                                            ] || key,
                                        children: value,
                                    }))}
                            />

                            <Flex.Column gap="medium">
                                <h4>Tags</h4>

                                <TagsWithCategories
                                    offeringCategory={
                                        item.offeringCategories
                                            ? item.offeringCategories[0]
                                            : undefined
                                    }
                                    values={tags}
                                />
                            </Flex.Column>

                            {geoPoint && placeDetails?.viewport ? (
                                <GoogleMap
                                    mapId={mapId}
                                    markerTitle={item.name}
                                    markerPosition={{
                                        lat: geoPoint.latitude,
                                        lng: geoPoint.longitude,
                                    }}
                                    viewport={placeDetails.viewport}
                                />
                            ) : (
                                item.googlePlaceId && <LoadingSpinner />
                            )}
                        </Flex.Column>

                        <div className="catalog-item__extra-spacer" />

                        <Flex.Column
                            gap="xlarge"
                            className={classNames(
                                item.archived && "catalog-item--archived",
                            )}
                        >
                            <h3
                                className={classNames(
                                    item.archived && "catalog-item--archived",
                                )}
                            >
                                <FlexInline gap="medium">
                                    Internal for Albertine{" "}
                                    <Icon.VpnKey.Small.N50.Fill />
                                </FlexInline>
                            </h3>

                            <InternalDetails
                                offeringCategories={item.offeringCategories}
                                notes={item.notes}
                                source={source}
                                suppliersData={suppliersData}
                                albertineVerified={item.albertineVerified}
                                recentlyOpened={item.recentlyOpened}
                            />

                            <h4>World of Albertine</h4>

                            <WorldOfAlbertineDetails
                                published={item.published}
                                discoverableForTenant={item.discoverableForTenant}
                            />
                        </Flex.Column>

                        <div className="catalog-item__extra-spacer" />

                        <Flex.Column gap="large">
                            <h4
                                className={classNames(
                                    item.archived && "catalog-item--archived",
                                )}
                            >
                                Admin
                            </h4>
                            <AdminDetails
                                item={item}
                                isAdmin={isAdmin}
                                onEdit={
                                    onEdit
                                        ? () => {
                                              onEdit(item);
                                          }
                                        : undefined
                                }
                                onView={
                                    onView
                                        ? () => {
                                              onView(item);
                                          }
                                        : undefined
                                }
                                onToggleArchive={onToggleArchive}
                            />
                        </Flex.Column>
                    </Flex.Column>
                </Flex.Column>
            </Flex.Column>

            <Flex.Column
                gap="large"
                className={classNames(
                    "catalog-item__feedback",
                    item.archived && "catalog-item--archived",
                )}
            >
                <h3 id={feedbackAnchor}>
                    <FlexInline gap="medium">
                        Feedback
                        <Icon.VpnKey.Small.N50.Fill />
                    </FlexInline>
                </h3>

                {reviews && reviews.average !== 0 ? (
                    <FlexInline gap="medium" alignSelf="start">
                        <ScoreTag ratingScore={reviews} />
                        <Label.XSmall.SemiBold.N50>
                            {singularPlural(
                                `${reviews.ratingsCount} review`,
                                `${reviews.ratingsCount} reviews`,
                                reviews.ratingsCount,
                            )}
                        </Label.XSmall.SemiBold.N50>
                    </FlexInline>
                ) : (
                    <Label.XSmall.SemiBold.N50>
                        No reviews yet
                    </Label.XSmall.SemiBold.N50>
                )}

                <div>
                    {item?.id && (
                        <MemberRatings showTitle={false} catalogId={item.id} />
                    )}
                </div>
            </Flex.Column>
        </Flex.Column>
    );
}

export default CatalogItemView;
